import React from 'react';
import {Box, Stack, styled} from '@mui/material';
import {
  Body,
  Button,
  Headline,
  Icon,
  Input,
  Modal,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import useCopyToClipboardWithFeedback from '@/hooks/useCopyToClipboardWithFeedback';

const TRANSLATION_PREFIX = 'views.creators.components.creator-action';

const HeadlineBox = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-start',
});

const SubtitleBody = styled(Body)({
  paddingInline: 16,
  whiteSpace: 'pre-wrap',
  textAlign: 'start',
});

const ShareableLinkBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

const ReadOnlyInput = styled(Input)({
  '& .MuiInputBase-input': {
    cursor: 'text',
    userSelect: 'all',
  },
});

function SuccessModal({
  title,
  subtitle,
  isOpen,
  close,
  link,
}: {
  title: string;
  subtitle: string;
  isOpen: boolean;
  close: () => void;
  link?: string;
}) {
  return (
    <Modal open={isOpen} handleClose={close} showCloseButton size="small">
      <Stack gap={3} width="100%">
        <HeadlineBox>
          <Headline size="md">{title}</Headline>
        </HeadlineBox>
        <SubtitleBody size="lg">{subtitle}</SubtitleBody>

        {link && <ShareableLink link={link} />}

        <Button
          appearance="neutral"
          mode="filled"
          size="medium"
          onClick={close}
        >
          {translate(`${TRANSLATION_PREFIX}.success-button`)}
        </Button>
      </Stack>
    </Modal>
  );
}

const LINK_COPIED_RESET_MS = 2000;

function ShareableLink({link}: {link: string}) {
  const {copied: linkCopied, copyToClipboard} =
    useCopyToClipboardWithFeedback(LINK_COPIED_RESET_MS);

  const handleCopyLink = () => copyToClipboard(link);

  return (
    <ShareableLinkBox>
      <ReadOnlyInput value={link} readOnly clearIcon={false} />
      <Button
        appearance="neutral"
        mode="tinted"
        size="medium"
        onClick={handleCopyLink}
        icon={
          <Icon name="Navigation-Copy" size="medium" appearance="neutral" />
        }
      >
        {linkCopied ? translate('Copied!') : translate('Copy Link')}
      </Button>
    </ShareableLinkBox>
  );
}

export default SuccessModal;
